import React, {useEffect, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import moment from 'moment';
import {AiFillDelete} from 'react-icons/ai';
import {IoMdEye} from 'react-icons/io';
import {FaPlus} from 'react-icons/fa';

import CustomTable from '../components/common/CustomTable';
import InputField from '../components/ui/InputField';
import {pageRoutes} from '../routes';
import {useGetEmployersQuery} from '../api/employer';
import {useDeleteUserMutation} from '../api/auth';
import {invalidateQueryV2} from '../services/reactQuery';
import AlertModal from '../components/AlertModal';
import {userType} from '../utils/constants';
import {SelectFieldHookForm} from '../components/ui/SelectField';
import CreateUserModal from '../components/CreateUserModal';
import useAuthStore from '../store/useAuthStore';

const ManageUser = () => {
  const [formData, setFormData] = useState({
    search: '',
    sort: null,
    sortBy: null,
    page: 1,
    limit: 10,
    role: userType[0].value, // Default to admin
  });

  const [removeEmployerId, setRemoveEmployerId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const user = useAuthStore((state) => state.user);

  const deleteUserMutation = useDeleteUserMutation();
  const {isLoading: isUserDeleteLoading} = deleteUserMutation;
  const {
    data: employersData,
    isFetching: isUserLoading,
    error,
  } = useGetEmployersQuery(formData);
  const {
    userData: allEmployers = [],
    totalItems = null,
    totalPages = null,
  } = employersData?.data || {userData: []};

  const {handleSubmit, control, watch, register} = useForm({
    mode: 'all',
    defaultValues: {
      role: userType[0],
    },
  });

  const handleFormSubmission = async (values) => {
    const {search, role} = values;
    let payload = {search};

    // add additinal fields in payload like any filters
    if (role?.value) payload = {...payload, role: role.value};

    setFormData((prev) => ({
      ...prev,
      ...payload,
    }));
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(handleFormSubmission));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  useEffect(() => {
    if (!isUserLoading && !allEmployers.length && !error) {
      setFormData((prev) => ({
        ...prev,
        page: prev.page === 1 ? 1 : prev.page - 1,
      }));
    }
  }, [isUserLoading, allEmployers, error]);

  const handleSort = (sortingDetails) => {
    const {page = null, sortBy, sortDirection = null} = sortingDetails;
    setFormData((prevFormData) => ({
      ...prevFormData,
      page: page || prevFormData.page,
      sortBy,
      sort: sortDirection,
    }));
  };

  const handleDeleteUser = (userId) => {
    deleteUserMutation.mutate(userId, {
      onSuccess: () => {
        setRemoveEmployerId(null);
        toast.success('Candidate removed successfully.');
        invalidateQueryV2(['candidateJobs']);
      },
      onError: (data) => toast.error(data.response.data.message),
    });
  };

  const tHeadData = [
    {
      id: 2,
      keyIndex: 'userName',
      label: 'User Name',
      sort: true,
      width: '30%',
    },
    {
      id: 3,
      keyIndex: 'role',
      label: 'Role',
      sort: true,
      width: '30%',
      render: (item) =>
        userType.map((user) => user.value === item && user.label) || '-',
    },
    {
      id: 5,
      keyIndex: 'createdAt',
      label: 'Joining Date',
      width: '30%',
      render: (item) => <>{moment(item).format('MM/DD/YYYY h:mm a')}</>,
      sort: true,
    },
    {
      id: 7,
      keyIndex: 'role',
      label: 'Action',
      width: '10%',
      render: (_, {_id}) => (
        <div className="flex gap-2">
          {/* <AiFillEdit size={20} /> */}
          <NavLink to={`${pageRoutes.employer}/${_id}`}>
            <IoMdEye size={23} />
          </NavLink>
          <button type="button" onClick={() => setRemoveEmployerId(_id)}>
            <AiFillDelete size={20} />
          </button>
        </div>
      ),
    },
  ];

  console.log('user:', user);
  return (
    <>
      <AlertModal
        type="warning"
        isOpen={removeEmployerId}
        onClose={() => setRemoveEmployerId(null)}
        title="Confirm this action"
        onCancel={() => setRemoveEmployerId(null)}
        onOK={() => handleDeleteUser(removeEmployerId)}
        okLoading={isUserDeleteLoading}
      >
        Are you sure you want to delete this user?
      </AlertModal>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <div className="px-8 py-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold capitalize">
            {userType.map((user) => user.value === formData.role && user.label)}
            {!!Number(totalItems) && `(${totalItems})`}
          </h3>
          {user?.role === 'admin' && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-main border border-transparent rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 "
            >
              <FaPlus className="mr-2" />
              Add User
            </button>
          )}
        </div>
        <form className="py-5 flex">
          <div className="flex justify-start">
            <InputField
              {...register('search')}
              type="search"
              placeholder="Search"
              className="w-60"
            />
          </div>
          <div className="h-full ml-3 justify-self-stretch ">
            <SelectFieldHookForm
              placeholder="type"
              control={control}
              name="role"
              options={userType}
            />
          </div>
        </form>

        <div className="bg-[white] p-10">
          <CustomTable
            tHeadData={tHeadData}
            tBodyData={allEmployers}
            handleSort={handleSort}
            pageCount={totalPages}
            currentPage={formData.page}
            loading={isUserLoading}
          />
        </div>
      </div>
    </>
  );
};

export default ManageUser;
